import { Option } from '@sapphire/result';

import { PROJECT_TYPE } from '@ps/survey-kernel';
import { Nullable } from '@ps/utils';

import type {
  GroupedParentStats,
  ProjectStats,
  Survey,
  TrackerParentStats,
} from '../models/survey.model';

function isTrackerParentProject(
  project: ProjectStats,
): project is TrackerParentStats {
  return (
    project?.parent === true &&
    'waveCount' in project &&
    typeof project.waveCount === 'number'
  );
}

function isGroupedParentProject(
  project: ProjectStats,
): project is GroupedParentStats {
  return (
    project?.parent === true &&
    !('waveCount' in project) &&
    Array.isArray(project.surveys)
  );
}

function resolveProject(survey: Survey): NonNullable<ProjectStats> {
  return Option.from(survey.project).unwrapOr({ parent: false });
}

export function resolveTrackerParent(
  survey: Survey,
): Nullable<TrackerParentStats> {
  return Option.from(resolveProject(survey))
    .filter(() => survey.projectType === PROJECT_TYPE.TRACKER)
    .filter(isTrackerParentProject)
    .unwrapOr(null);
}

export function resolveGroupedParent(
  survey: Survey,
): Nullable<GroupedParentStats> {
  return Option.from(resolveProject(survey))
    .filter(() => survey.projectType === PROJECT_TYPE.GROUPED)
    .filter(isGroupedParentProject)
    .unwrapOr(null);
}

export function isTrackerParent(survey: Survey): boolean {
  return Option.from(resolveTrackerParent(survey)).isSome();
}

export function isGroupedParent(survey: Survey): boolean {
  return Option.from(resolveGroupedParent(survey)).isSome();
}

export function resolveSurveyProjectStats(survey: Survey): {
  groupedProject: Nullable<GroupedParentStats>;
  trackerProject: Nullable<TrackerParentStats>;
} {
  return {
    groupedProject: resolveGroupedParent(survey),
    trackerProject: resolveTrackerParent(survey),
  };
}
